import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail } from 'lucide-react';

const Footer = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');

  const handleNewsletterSubmit = async () => {
    if (!newsletterEmail || !/\S+@\S+\.\S+/.test(newsletterEmail)) {
      setNewsletterStatus('Please enter a valid email');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail })
      });
      if (response.ok) {
        setNewsletterStatus('Welcome to the hive!');
        setNewsletterEmail('');
      } else {
        setNewsletterStatus('Something went wrong. Try again.');
      }
    } catch {
      setNewsletterStatus('Something went wrong. Try again.');
    }
  };
  return (
    <footer className="bg-muted py-20 mt-32" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_bee-harvest-6/artifacts/5m5uevnv_Ricky%27s%20Bees%20Logo.png" 
              alt="Ricky's Bees" 
              className="h-32 md:h-40 w-auto mb-4"
            />
            <p className="text-foreground/70 leading-relaxed">
              Raw Australian honey from Victoria. From hive to jar, nothing added, nothing taken away.
            </p>
          </div>

          <div className="md:pl-16">
            <h4 className="text-lg font-semibold text-foreground mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <Link to="/products" className="block text-foreground/70 hover:text-primary transition-colors" data-testid="footer-shop">
                Shop Honey
              </Link>
              <Link to="/about" className="block text-foreground/70 hover:text-primary transition-colors" data-testid="footer-about">
                About Us
              </Link>
              <Link to="/recipes" className="block text-foreground/70 hover:text-primary transition-colors" data-testid="footer-recipes">
                Recipes
              </Link>
              <Link to="/refund-policy" className="block text-foreground/70 hover:text-primary transition-colors">
                Refund Policy
              </Link>
              <Link to="/shipping-policy" className="block text-foreground/70 hover:text-primary transition-colors">
                Shipping Policy
              </Link>
              <Link to="/faq" className="block text-foreground/70 hover:text-primary transition-colors" data-testid="footer-faq">
                FAQ
              </Link>
              <a href="/contact" className="block text-foreground/70 hover:text-primary transition-colors" data-testid="footer-contact">
                Contact Us
              </a>
              <a href="/collaborate" className="block text-foreground/70 hover:text-primary transition-colors" data-testid="footer-collaborate">
                Collaborate with Us
              </a>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-foreground mb-4">Join the Hive</h4>
            <p className="text-foreground/70 mb-4 text-sm">
              Subscribe to our newsletter for honey tips, recipes and exclusive offers.
            </p>
            <div className="flex space-x-2">
              <input
                type="email"
                placeholder="Your email"
                value={newsletterEmail}
                onChange={(e) => { setNewsletterEmail(e.target.value); setNewsletterStatus(''); }}
                className="flex-1 px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="newsletter-email-input"
              />
              <button
                onClick={handleNewsletterSubmit}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                data-testid="newsletter-submit-button"
              >
                Join
              </button>
            </div>
            {newsletterStatus && (
              <p className={`text-sm mt-2 ${newsletterStatus === 'Welcome to the hive!' ? 'text-green-600' : 'text-red-500'}`} data-testid="newsletter-status">
                {newsletterStatus}
              </p>
            )}
          </div>
        </div>

        <div className="border-t border-border pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
            <p className="text-foreground/60 text-sm">
              © 2026 Ricky's Bees. All rights reserved.
            </p>
            <div className="flex items-center space-x-4 text-sm">
              <Link to="/privacy-policy" className="text-foreground/60 hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <span className="text-foreground/40">•</span>
              <Link to="/terms" className="text-foreground/60 hover:text-primary transition-colors">
                Terms & Conditions
              </Link>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <a
              href="https://instagram.com/rickys_bees"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/60 hover:text-primary transition-colors"
              data-testid="instagram-link"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="mailto:sales@rickysbees.com.au"
              className="text-foreground/60 hover:text-primary transition-colors"
              data-testid="email-link"
            >
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
